import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Facebook, Instagram, Linkedin } from 'lucide-react';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const serviceAreas = [
    { name: 'New Berlin, WI', path: '/landscaping-new-berlin-wi' },
    { name: 'Brookfield, WI', path: '/landscaping-brookfield-wi' },
    { name: 'Menomonee Falls, WI', path: '/landscaping-menomonee-falls-wi' },
    { name: 'Waukesha, WI', path: '/landscaping-waukesha-wi' },
    { name: 'Muskego, WI', path: '/landscaping-muskego-wi' },
    { name: 'Milwaukee, WI', path: '/landscaping-milwaukee-wi' },
  ];

  const services = [
    { name: 'Landscape Services', path: '/services/landscape' },
    { name: 'Landscape Design', path: '/services/landscape-design' },
    { name: 'Hardscape', path: '/services/hardscape' },
    { name: 'Lawn Maintenance', path: '/services/lawn-maintenance' },
    { name: 'Tree Service', path: '/services/tree-service' },
    { name: 'Snow & Ice Removal', path: '/snow-removal' },
    { name: 'Power Sweeping', path: '/services/power-sweeping' },
  ];

  const quickLinks = [
    { name: 'Residential', path: '/residential' },
    { name: 'Commercial', path: '/commercial' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Blog', path: '/blog' },
    { name: 'About Us', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <footer className="bg-stone-900 text-stone-300" data-testid="footer">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <img 
              src="https://customer-assets.emergentagent.com/job_snow-landscape-depot/artifacts/n8qj5gl1_LOGO.png" 
              alt="WayBetter Landscaping & Snowplowing" 
              className="h-20 w-auto mb-6"
              width="160"
              height="80"
              loading="lazy"
            />
            <p className="font-manrope text-sm leading-relaxed mb-4">
              Dependable, honest, and professional landscaping and snow removal services for Metro Milwaukee since 2014.
            </p>
            <p className="font-barlow text-accent uppercase tracking-wider text-sm font-bold">
              "You Grow It, We Mow It - We Want Your Green!"
            </p>
            
            {/* Social Links */}
            <div className="flex gap-4 mt-6">
              <a 
                href="https://www.facebook.com/waybetterlandandsnow/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-stone-800 hover:bg-primary rounded flex items-center justify-center transition-colors"
                aria-label="Facebook"
                data-testid="footer-facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a 
                href="#" 
                className="w-10 h-10 bg-stone-800 hover:bg-primary rounded flex items-center justify-center transition-colors"
                aria-label="Instagram"
                data-testid="footer-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a 
                href="#" 
                className="w-10 h-10 bg-stone-800 hover:bg-primary rounded flex items-center justify-center transition-colors"
                aria-label="LinkedIn"
                data-testid="footer-linkedin"
              >
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-barlow font-bold text-white uppercase tracking-wider text-lg mb-6">Our Services</h3>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.name}>
                  <Link 
                    to={service.path}
                    className="text-stone-300 hover:text-accent transition-colors font-manrope text-sm"
                    data-testid={`footer-service-${service.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links & Service Areas */}
          <div>
            <h3 className="font-barlow font-bold text-white uppercase tracking-wider text-lg mb-6">Quick Links</h3>
            <ul className="space-y-3 mb-8">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.path}
                    className="text-stone-300 hover:text-accent transition-colors font-manrope text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>

            <h3 className="font-barlow font-bold text-white uppercase tracking-wider text-lg mb-4">Service Areas</h3>
            <ul className="space-y-2">
              {serviceAreas.map((area) => (
                <li key={area.name}>
                  <Link 
                    to={area.path}
                    className="text-stone-300 hover:text-accent transition-colors font-manrope text-sm flex items-center gap-2"
                  >
                    <MapPin className="w-3 h-3 text-accent" />
                    {area.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-barlow font-bold text-white uppercase tracking-wider text-lg mb-6">Contact Us</h3>
            <ul className="space-y-4">
              <li>
                <a 
                  href="tel:414-795-3610"
                  className="flex items-start gap-3 text-stone-300 hover:text-accent transition-colors"
                  data-testid="footer-phone"
                >
                  <Phone className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span className="font-manrope text-sm">(414) 795-3610<br /><span className="text-stone-400">Call or Text</span></span>
                </a>
              </li>
              <li>
                <a 
                  href="mailto:info@waybetterlandandsnow.com"
                  className="flex items-start gap-3 text-stone-300 hover:text-accent transition-colors"
                  data-testid="footer-email"
                >
                  <Mail className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span className="font-manrope text-sm">info@waybetterlandandsnow.com</span>
                </a>
              </li>
              <li className="flex items-start gap-3 text-stone-300">
                <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <span className="font-manrope text-sm">
                  N92W17420 Appleton Avenue<br />
                  Suite 108<br />
                  Menomonee Falls, WI 53051
                </span>
              </li>
              <li className="flex items-start gap-3 text-stone-300">
                <Clock className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <span className="font-manrope text-sm">
                  Mon-Fri: 8:00am-5:00pm<br />
                  Sat: 9:00am-4:30pm<br />
                  Sun: Closed
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Trust Bar */}
      <div className="border-t border-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-wrap justify-center gap-8 items-center text-stone-400 font-barlow uppercase tracking-wider text-sm">
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 bg-accent rounded-full"></span>
              SBE Certified
            </span>
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 bg-accent rounded-full"></span>
              10+ Years Experience
            </span>
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 bg-accent rounded-full"></span>
              Satisfaction Guaranteed
            </span>
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 bg-accent rounded-full"></span>
              Free Assessment
            </span>
          </div>
        </div>
      </div>

      {/* Copyright */}
      <div className="border-t border-stone-800 bg-stone-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-stone-400 text-sm font-manrope">
            <p>&copy; {currentYear} WayBetter Landscaping & Snowplowing LLC. All rights reserved.</p>
            <div className="flex gap-6">
              <Link to="/contact" className="hover:text-accent transition-colors">Contact</Link>
              <a href="https://waybetterlandandsnow.com" target="_blank" rel="noopener noreferrer" className="hover:text-accent transition-colors">Original Website</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
